import { COMMON, PROFILE_FIELDS } from '../../constants/strings';
import {
  PROFILE_FIELDS_TYPES,
  INSURANCE_ELIGIBILITY_FIELDS_TYPES,
} from '../../constants/formFields';
import { LENGTH_VALUES } from '../../constants/validations';
import amountValidator from '../validations/amountValidator';
import { IDS } from '../../constants/ids';
import { VISIT_DOCUMENTS_TYPES } from '../../components/Healthboard/Components/VisitDocuments/AddDocumentForm/constants';

export const FIELD_FILLING_TYPES = {
  REQUIRED: 'required',
  OPTIONAL: 'optional',
};

const commonProps = {
  className: 'mt3',
};

const getLabelWithOptional = formatMessage => (text, type) => {
  const optText = formatMessage(COMMON.optional);
  const formattedText = formatMessage(text);

  if (type === FIELD_FILLING_TYPES.OPTIONAL)
    return `${formattedText} ${optText}`;

  return formattedText;
};
// TODO use FIELD_FILLING_TYPES for required and optional fields
export function getFieldProps(formatMessage, onChange) {
  const labelWithOptional = getLabelWithOptional(formatMessage);

  return (field, type) => {
    switch (field) {
      case PROFILE_FIELDS_TYPES.EMAIL: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.EMAIL,
          type: 'text',
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.email, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.email),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.PHONE_NUMBER: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.PHONE_NUMBER,
          type: 'number',
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.mobilePhone, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.mobilePhone),
          onChange: (name, value) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.HOME_PHONE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.HOME_PHONE,
          type: 'number',
          limit: LENGTH_VALUES.HOME_PHONE.MAX,
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.homePhone, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.homePhone),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.FIRST_NAME: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.FIRST_NAME,
          required: true,
          type: 'text',
          limit: LENGTH_VALUES.FIRST_NAME.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.firstName),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.firstName),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.MIDDLE_NAME: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.MIDDLE_NAME,
          type: 'text',
          limit: LENGTH_VALUES.MIDDLE_NAME.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.middleName),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.middleName),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.LAST_NAME: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.LAST_NAME,
          required: true,
          type: 'text',
          limit: LENGTH_VALUES.LAST_NAME.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.lastName),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.lastName),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.NAME_SUFFIX: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.NAME_SUFFIX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.nameSuffix),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.nameSuffix),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.GENDER: {
        return {
          ...commonProps,
          required: true,
          label: formatMessage(PROFILE_FIELDS.fieldNames.gender),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.gender),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.DOB: {
        return {
          ...commonProps,
          required: true,
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.LOCAL_UID: {
        return {
          ...commonProps,
          limit: LENGTH_VALUES.MRNS,
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.mrns, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.mrns),
          onChange: value => onChange({ [field]: value.trim() }),
          autoComplete: 'new-password',
          name: 'mrns',
          id: 'mrns',
        };
      }

      case PROFILE_FIELDS_TYPES.SSN: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.SSN,
          limit: LENGTH_VALUES.SSN,
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.ssnLong, type),
          placeholder: formatMessage({ id: 'profile.fields.ssnPlaceHolder' }),
          onChange: (name, value) => onChange({ [field]: value }),
          autoComplete: 'new-password',
          name: 'ssn',
        };
      }

      case PROFILE_FIELDS_TYPES.ADDRESS_1: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.ADDRESS_1,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          type: 'text',
          limit: LENGTH_VALUES.ADDRESS_1.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.address1),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.address1),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.ADDRESS_2: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.ADDRESS_2,
          type: 'text',
          limit: LENGTH_VALUES.ADDRESS_2.MAX,
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.address2, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.address2),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.CITY: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.CITY,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          type: 'text',
          limit: LENGTH_VALUES.CITY.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.city),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.city),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.STATE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.STATE,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage(PROFILE_FIELDS.fieldNames.state),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.state),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.POSTAL_CODE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.POSTAL_CODE,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          type: 'text',
          label: formatMessage(PROFILE_FIELDS.fieldNames.postalCode),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.postalCode),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.IS_HOME_LOCATION_DIFFERENT: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.IS_HOME_LOCATION_DIFFERENT,
          size: 16,
          style: { display: 'flex', alignItems: 'center' },
          label: formatMessage(
            PROFILE_FIELDS.fieldNames.isHomeLocationDifferent,
          ),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.COUNTRY: {
        return {
          ...commonProps,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage(PROFILE_FIELDS.fieldNames.country),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.country),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.RELATION: {
        return {
          ...commonProps,
          label: formatMessage(PROFILE_FIELDS.fieldNames.relation),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.relation),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.ROLE: {
        return {
          ...commonProps,
          multi: false,
          needUpdateDefaultValue: true,
          label: formatMessage({ id: 'profile.fields.role' }),
          placeholder: formatMessage({
            id: 'profile.fields.role.placeholder',
          }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.RELATIONSHIP: {
        return {
          ...commonProps,
          needUpdateDefaultValue: true,
          multi: false,
          label: formatMessage({ id: 'profile.fields.relationship' }),
          placeholder: formatMessage({
            id: 'profile.fields.relationship.placeholder',
          }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.LOGIN: {
        return {
          ...commonProps,
          type: 'text',
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'fields.login' }),
          placeholder: formatMessage({ id: 'fields.login.placeholder' }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.EMAIL_OR_PHONE: {
        return {
          ...commonProps,
          type: 'text',
          label: formatMessage({ id: 'profile.fields.emailOrPhone' }),
          placeholder: formatMessage({ id: 'profile.fields.emailOrPhone' }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.USERNAME: {
        return {
          ...commonProps,
          type: 'text',
          label: formatMessage({
            id: 'auth.recoverPassword.primaryContacts.username',
          }),
          placeholder: formatMessage({
            id: 'auth.recoverPassword.primaryContacts.username',
          }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.TERMS_AND_CONDITIONS: {
        return {
          ...commonProps,
          acceptable: true,
          onChange: e =>
            onChange({ [field]: e.target.checked ? 'true' : null }),
        };
      }

      case PROFILE_FIELDS_TYPES.REASON_TYPE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.REASON_TYPE,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'fields.reasonType' }),
          placeholder: formatMessage({ id: 'fields.reasonType.placeholder' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.REASON_VISIT: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.REASON_VISIT,
          label: formatMessage({ id: 'fields.reasonVisit' }),
          placeholder: formatMessage({ id: 'fields.reasonVisit' }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.REASON_NOTE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.REASON_NOTE,
          limit: LENGTH_VALUES.REASON_NOTE.MAX,
          textarea: true,
          label: labelWithOptional({ id: 'common.description' }, type),
          placeholder: formatMessage({ id: 'common.description' }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.LOCATION: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.LOCATION,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'dashboard.filter.location' }),
          placeholder: formatMessage({
            id: 'dashboard.record.locationExample',
          }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.SERVICE: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.SERVICE,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'dashboard.filter.service' }),
          placeholder: formatMessage({ id: 'dashboard.record.serviceExample' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.PROVIDER: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.PROVIDER,
          label: labelWithOptional({ id: 'profile.actions.provider' }, type),
          placeholder: formatMessage({ id: 'common.searchByName' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.ROOM: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.ROOM,
          limit: LENGTH_VALUES.ROOM.MAX,
          label: labelWithOptional({ id: 'dashboard.record.room' }, type),
          placeholder: formatMessage({ id: 'dashboard.record.room' }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.PAYMENT_STATUS: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.PAYMENT_STATUS,
          label: labelWithOptional(
            { id: 'dashboard.record.paymentStatus' },
            type,
          ),
          placeholder: formatMessage({ id: 'dashboard.record.paymentStatus' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.COPAY: {
        return {
          ...commonProps,
          type: 'text',
          inputMode: 'decimal',
          limit: LENGTH_VALUES.COPAY.MAX,
          label: labelWithOptional(
            { id: 'virtualVisit.details.paymentAmountUsd' },
            type,
          ),
          placeholder: formatMessage({
            id: 'virtualVisit.details.paymentAmount',
          }),
          errorText: formatMessage({ id: 'errors.appointment.copay' }),
          validationRule: amountValidator,
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.CHAT_NAME: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.CHAT_NAME,
          type: 'text',
          limit: LENGTH_VALUES.CHAT_NAME.MAX,
          label: formatMessage({ id: 'fields.chatName' }),
          placeholder: formatMessage({
            id: 'fields.chatName.placeholder',
          }),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case VISIT_DOCUMENTS_TYPES.CATEGORY: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.CATEGORY,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'common.category' }),
          placeholder: formatMessage({ id: 'common.category' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case VISIT_DOCUMENTS_TYPES.SUB_CATEGORY: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.SUB_CATEGORY,
          required: type === FIELD_FILLING_TYPES.REQUIRED,
          label: formatMessage({ id: 'common.subCategory' }),
          placeholder: formatMessage({ id: 'common.subCategory' }),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.ACCEPTED_TERMS_AND_CONDITIONS:
      case PROFILE_FIELDS_TYPES.ACCEPTED_PRIVACY_POLICY: {
        return {
          ...commonProps,
          ...field,
          id: IDS.FORM_FIELDS.SUB_CATEGORY,
          termsName: PROFILE_FIELDS_TYPES.ACCEPTED_TERMS_AND_CONDITIONS,
          privacyName: PROFILE_FIELDS_TYPES.ACCEPTED_PRIVACY_POLICY,
          onChange,
        };
      }

      case INSURANCE_ELIGIBILITY_FIELDS_TYPES.MEMBER_ID: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.MEMBER_ID,
          required: true,
          type: 'text',
          limit: LENGTH_VALUES.MEMBER_ID.MAX,
          label: formatMessage(PROFILE_FIELDS.fieldNames.memberId),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.memberId),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case INSURANCE_ELIGIBILITY_FIELDS_TYPES.GROUP_NUMBER: {
        return {
          ...commonProps,
          id: IDS.FORM_FIELDS.GROUP_NUMBER,
          type: 'text',
          limit: LENGTH_VALUES.GROUP_NUMBER.MAX,
          label: labelWithOptional(PROFILE_FIELDS.fieldNames.groupNumber, type),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.groupNumber),
          onChange: value => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.RACE: {
        return {
          ...commonProps,
          required: false,
          label: formatMessage(PROFILE_FIELDS.fieldNames.race),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.race),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      case PROFILE_FIELDS_TYPES.ETHNICITY: {
        return {
          ...commonProps,
          required: false,
          label: formatMessage(PROFILE_FIELDS.fieldNames.ethnicity),
          placeholder: formatMessage(PROFILE_FIELDS.fieldNames.ethnicity),
          onChange: ({ value }) => onChange({ [field]: value }),
        };
      }

      default: {
        return {};
      }
    }
  };
}



// WEBPACK FOOTER //
// ./src/utils/fields/getFieldProps.jsx